import React from 'react';
import { isEmpty } from 'lodash';

import CardDialog from '~/components/CardDialog';
import ClaimLink from '~/components/ClaimLink';
import CliveButton from '~/components/Clive/CliveButton/CliveButton';
import CliveRecommendationPanel from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanel';
import CliveRecommendationPanelButton from '~/components/Clive/CliveRecommendationPanel/CliveRecommendationPanelButton';
import RecommendedPolicyDisplay from '~/components/Clive/CliveRecommendationPanel/RecommendedPolicyDisplay';
import CliveSummaryCard from '~/components/Clive/SummaryCard/CliveSummaryCard';
import type { CliveRecommendationAvatarType } from '~/components/Clive/types';
import Grid from '~/components/core/Atomic/Grid/Grid';
import { useFnolProgressTimeline } from '~/components/Fnol/FnolAiInbox/hooks/useFnolProgressTimeline';
import { determineFnolRecommendationState } from '~/components/Fnol/FnolAiInbox/tabs/fnolAiRecommendationStateUtils';
import { FnolRecommendationState } from '~/components/Fnol/FnolAiInbox/types';
import PlainTable from '~/components/PlainTable';
import ProgressTimeline from '~/components/ProgressTimeline/ProgressTimeline';
import type { FnolInboxRecord, FnolInboxRecordRelatedClaim } from '~/server_shared/openapi/data-contracts';
import { FnolInboxRecordFnolTaskErrorType } from '~/server_shared/openapi/data-contracts';

// Stands in until the FNOL run has said anything of its own.
const FNOL_PENDING_STATUS_TEXT = 'Processing the FNOL...';

interface FnolInboxItemAiAnalysisBannerProps {
  selectedInboxItem: FnolInboxRecord;
  onAttachToExistingClaim: (claimId: number, extraParams: Record<string, unknown>) => Promise<void>;
  onNewLoss?: () => void;
  onPolicySelection?: (inboxItem: FnolInboxRecord) => void;
  onRetry?: (inboxItemId: number) => void;
  onTakeLossManually?: () => void;
  onProcessingFinished?: () => void;
}

/**
 * Gets all content for the FNOL recommendation panel based on state
 *
 * @param state - The current FNOL recommendation state
 * @param selectedInboxItem - The inbox item containing analysis data and related claims
 * @param callbacks - Object containing callback functions for different actions
 * @param communicationType - Type of communication (default: 'email')
 * @returns Object containing subtitle, explanation, and actionComponent for the panel
 *
 * @description
 * This function consolidates all state-specific content generation for the recommendation panel.
 * It determines the appropriate subtitle, explanation content, and action component based on
 * the current state, handling dynamic content for states like RECOMMENDATION_EXISTS.
 */
const getStateContent = (
  state: FnolRecommendationState,
  selectedInboxItem: FnolInboxRecord,
  callbacks: {
    onAttachToClaim?: (claimId: number, extraParams: Record<string, unknown>) => void;
    onViewClaims?: () => void;
    onNewLoss?: () => void;
    onPolicySelection?: (inboxItem: FnolInboxRecord) => void;
    onRetry?: (inboxItemId: number) => void;
    onTakeLossManually?: () => void;
  },
  communicationType: string = 'email'
): {
  title: string;
  subtitle: string;
  explanation: React.ReactNode;
  actionComponent: React.ReactNode;
  avatarType?: CliveRecommendationAvatarType;
  content?: React.ReactNode;
} => {
  const { onAttachToClaim, onViewClaims, onNewLoss, onPolicySelection } = callbacks;

  switch (state) {
    case FnolRecommendationState.PROCESSING:
      return {
        title: 'Email analysis in progress...',
        subtitle:
          'I\u2019m reviewing this email. A detailed summary and FNOL draft will be available once processing is complete. This may take a few minutes.',
        explanation: null,
        actionComponent: null,
        avatarType: 'loading',
      };

    case FnolRecommendationState.PROCESSING_FAILED: {
      // Handle different error types for processing failed state
      const errorType = selectedInboxItem.error_type;

      if (errorType === FnolInboxRecordFnolTaskErrorType.GeneralError) {
        return {
          title: 'Something Went Wrong',
          subtitle:
            'I wasn\u2019t able to complete the draft due to a temporary issue. Please try again, or continue without my help if the issue persists.',
          explanation: null,
          actionComponent: callbacks.onRetry ? (
            <CliveRecommendationPanelButton
              text="Try Again"
              onClick={() => callbacks.onRetry?.(selectedInboxItem.id)}
            />
          ) : null,
        };
      }

      return {
        title: 'No Draft Result',
        subtitle:
          'I reviewed the information but wasn\u2019t able to generate a draft. Please select the next action to continue without my help.',
        explanation: null,
        actionComponent: callbacks.onTakeLossManually ? (
          <CliveRecommendationPanelButton text="Take Loss Manually" onClick={callbacks.onTakeLossManually} />
        ) : null,
      };
    }

    case FnolRecommendationState.POLICY_NOT_FOUND_BY_AI:
    case FnolRecommendationState.UNKNOWN_SUBORG_LOB_SELECT_POLICY:
      return {
        title: 'No Policy Found',
        subtitle:
          'I couldn\u2019t associate this communication with a policy, so no draft was created. Please assign a policy.',
        explanation: null,
        actionComponent: onPolicySelection ? (
          <CliveRecommendationPanelButton text="Find Policy" onClick={() => onPolicySelection(selectedInboxItem)} />
        ) : null,
      };

    case FnolRecommendationState.RECOMMENDATION_EXISTS: {
      // Handle dynamic content based on related claims
      const { related_claims: relatedClaims } = selectedInboxItem?.analysis_extra || {};
      const relatedClaimsCount = relatedClaims?.length || 0;
      const firstClaim = relatedClaims ? relatedClaims[0] : null;

      let title: string = '';
      let subtitle: string;
      let explanation: React.ReactNode;
      let actionComponent: React.ReactNode;

      // Create policy display component if policy exists
      const policyDisplay =
        selectedInboxItem.policy && onPolicySelection ? (
          <RecommendedPolicyDisplay
            policyNumber={selectedInboxItem.policy.policy_number}
            onPolicySelection={() => onPolicySelection(selectedInboxItem)}
            tooltipText="Changing the policy will reprocess the FNOL with new policy details"
          />
        ) : null;

      if (firstClaim && relatedClaimsCount === 1) {
        subtitle = `This ${communicationType} refers to an existing claim`;
        explanation = (
          <>
            <ul>
              <li>{`This ${communicationType} seems to be related to an existing claim `}</li>
            </ul>
            <ClaimLink
              claimId={relatedClaims?.[0].claim_id as number}
              linkText={relatedClaims?.[0].claim_id_display as string}
            />
          </>
        );
        actionComponent = onAttachToClaim ? (
          <CliveRecommendationPanelButton
            text="Attach to Claim"
            onClick={() => onAttachToClaim(firstClaim.claim_id, {})}
          />
        ) : null;
      } else if (relatedClaimsCount > 1) {
        subtitle = 'A few matches were found to existing claims';
        explanation = (
          <>
            This email seems to be related to an existing claim, a few matches were found. View the claims and attach
            the email to the correct claim.
          </>
        );
        actionComponent = onViewClaims ? (
          <CliveRecommendationPanelButton text="View claims" onClick={onViewClaims} />
        ) : null;
      } else {
        title = 'FNOL Draft Ready for Review';
        const hasFnolSummary = Boolean(selectedInboxItem.fnol_summary);

        subtitle = hasFnolSummary
          ? `I\u2019ve prepared a draft for you. Click Create FNOL to review and finalize it. A summary is available in the expandable section below.`
          : `I\u2019ve prepared a draft for you. Click Create FNOL to review and finalize it.`;

        explanation = hasFnolSummary ? (
          <CliveSummaryCard title="FNOL Summary" summaryText={selectedInboxItem.fnol_summary!} />
        ) : null;

        actionComponent = onNewLoss ? <CliveRecommendationPanelButton text="Create FNOL" onClick={onNewLoss} /> : null;
      }

      return {
        title,
        subtitle,
        explanation,
        actionComponent,
        content: policyDisplay,
      };
    }

    default:
      return {
        title: '',
        subtitle: '',
        explanation: null,
        actionComponent: null,
      };
  }
};

function RelatedClaimsCardDialog({
  claims,
  onClose,
  onAttachToClaim,
}: {
  claims: FnolInboxRecordRelatedClaim[];
  onClose: () => void;
  onAttachToClaim: ((claimId: number, extraParams: Record<string, unknown>) => void) | undefined;
}) {
  const columnData = [
    {
      id: 'claim_id',
      numeric: false,
      label: 'Claim',
      specialCell: (claim: FnolInboxRecordRelatedClaim) => (
        <ClaimLink claimId={claim.claim_id} linkText={claim.claim_id_display} />
      ),
    },
    {
      id: 'action',
      numeric: false,
      label: '',
      specialCell: (claim: FnolInboxRecordRelatedClaim) => (
        <CliveButton
          text="Attach to Claim"
          onClick={async () => {
            onAttachToClaim && (await onAttachToClaim(claim.claim_id, { should_create_new_contact: true }));
            onClose();
          }}
        />
      ),
    },
  ];

  return (
    <CardDialog
      isDialog
      title="Claims Found"
      subheader="This email might be related to one of the following claims"
      maxWidth="sm"
      fullWidth
      onClose={onClose}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PlainTable columns={columnData} rows={claims} />
        </Grid>
      </Grid>
    </CardDialog>
  );
}

/**
 * FNOL Inbox Item AI Analysis Banner Component
 *
 * @param props - Component props
 * @param props.selectedInboxItem - The currently selected FNOL inbox item to analyze
 * @param props.onAttachToExistingClaim - Callback when user attaches email to existing claim
 * @param props.onNewLoss - Callback when user creates new FNOL
 * @param props.onPolicySelection - Callback when user selects a policy
 *
 * @description
 * This component displays Clive's AI analysis and recommendations for FNOL inbox items.
 * It uses a state-driven approach to determine the appropriate display and actions:
 *
 * States handled:
 * - PROCESSING: Shows processing indicator while AI analyzes the email
 * - PROCESSING_FAILED: Shows error message when AI processing fails
 * - UNKNOWN_SUBORG_LOB_SELECT_POLICY: Prompts user to select policy before processing
 * - POLICY_NOT_FOUND_BY_AI: Informs user that policy search found no results
 * - RECOMMENDATION_EXISTS: Shows AI recommendations based on analysis
 *
 * The component automatically determines the current state using the inbox item's
 * properties and displays appropriate content from the centralized display configuration.
 *
 * @example
 * ```tsx
 * <FnolInboxItemAiAnalysisBanner
 *   selectedInboxItem={inboxItem}
 *   onAttachToExistingClaim={handleAttach}
 *   onNewLoss={handleNewLoss}
 *   onPolicySelection={handlePolicySelection}
 * />
 * ```
 */
export const FnolInboxItemAiAnalysisBanner: React.FC<FnolInboxItemAiAnalysisBannerProps> = ({
  selectedInboxItem,
  onAttachToExistingClaim,
  onNewLoss,
  onPolicySelection,
  onRetry,
  onTakeLossManually,
  onProcessingFinished,
}) => {
  const [relatedClaimsOpen, setRelatedClaimsOpen] = React.useState(false);

  const communicationType = 'email'; // NOTE: currently only email is supported

  const relatedClaims = selectedInboxItem.analysis_extra?.related_claims ?? [];

  // Determine the current state using the new state determination logic
  const currentState = determineFnolRecommendationState(selectedInboxItem);

  const {
    entries: progressEntries,
    status: progressStatus,
    shouldDisplay: hasProgressTimeline,
  } = useFnolProgressTimeline({
    inboxItemId: selectedInboxItem.id,
    isLive: currentState === FnolRecommendationState.PROCESSING,
    onRunFinished: onProcessingFinished,
  });

  const handleAttachToClaim = (claimId: number, attachExtraParams: Record<string, unknown>) =>
    onAttachToExistingClaim(claimId, {
      ...attachExtraParams,
      should_create_new_contact: true,
    });

  const onViewClaims = () => setRelatedClaimsOpen(true);

  // Get all content for the current state (subtitle, explanation, action component)
  const { title, subtitle, explanation, actionComponent, avatarType, content } = getStateContent(
    currentState,
    selectedInboxItem,
    {
      onAttachToClaim: handleAttachToClaim,
      onViewClaims,
      onNewLoss,
      onPolicySelection,
      onRetry,
      onTakeLossManually,
    },
    communicationType
  );

  const hasRecommendationContent = subtitle || explanation || actionComponent;

  if (!hasRecommendationContent) {
    return null;
  }

  return (
    <>
      <CliveRecommendationPanel
        title={title}
        subtitle={subtitle}
        actionComponent={actionComponent}
        content={content}
        avatarType={avatarType}
        openByDefault={hasProgressTimeline}
      >
        {explanation}
        {hasProgressTimeline ? (
          <ProgressTimeline pendingText={FNOL_PENDING_STATUS_TEXT} entries={progressEntries} status={progressStatus} />
        ) : null}
      </CliveRecommendationPanel>
      {relatedClaimsOpen && !isEmpty(relatedClaims) ? (
        <RelatedClaimsCardDialog
          claims={relatedClaims}
          onClose={() => setRelatedClaimsOpen(false)}
          onAttachToClaim={handleAttachToClaim}
        />
      ) : null}
    </>
  );
};

export default FnolInboxItemAiAnalysisBanner;
